<template lang="pug">
v-card.custom-card(outlined, style="position: relative")
  v-progress-linear.empresas-loading-bar(
    v-if="carregando",
    indeterminate,
    color="blue",
    height="2"
  )
  //- Estado vazio: nenhum cliente selecionado
  v-card-text.text-center.py-8(v-if="!temCliente && !carregando")
    v-icon.empty-state-icon(size="48", color="blue lighten-3") mdi-domain
    .empty-state-title.mt-3 {{ textoVazioTitulo }}
    .empty-state-subtitle.mt-1 {{ textoVazioSubtitulo }}

  //- Conteúdo normal
  v-card-text.pa-0(v-else)
    .d-flex.align-center.justify-space-between.gap-2.empresas-header
      .empresas-titulo {{ titulo }}
      .pl-4(style="font-size: 12px") {{ subtitulo }}
      v-spacer
      .empresa-selecionada-resumo.d-flex.align-center.mr-3(
        v-if="!visivel && empresaSelecionada",
        :title="`Empresa selecionada: ${empresaSelecionada.codigoempresa} - ${empresaSelecionada.fantasia || empresaSelecionada.razaosocial}`"
      )
        .empresa-numero.empresa-numero--resumo(
          :class="{ 'empresa-numero--inativa': empresaSelecionada.empresaativa === false }",
          :style="estiloNumero(empresaSelecionada)"
        ) {{ empresaSelecionada.codigoempresa }}
        .empresa-selecionada-nome.text-truncate {{ empresaSelecionada.fantasia || empresaSelecionada.razaosocial }}
      v-btn.btn-action.text-none(
        v-if="podeClonar",
        small,
        outlined,
        :disabled="!temCliente || empresas.length < 2",
        @click="$emit('clonar')"
      )
        v-icon(x-small, left) mdi-content-copy
        | {{ textoClonar }}
      v-tooltip(bottom)
        template(v-slot:activator="{ on, attrs }")
            v-btn.btn-action.btn-action--icone.text-none(
              v-bind="attrs",
              v-on="on",
              small,
              outlined,
              @click="$emit('toggle-visivel')"
            )
              v-icon(small) {{ visivel ? "mdi-chevron-double-up" : "mdi-chevron-double-down" }}
        span {{ visivel ? "Recolher" : "Expandir" }}
    .empresas-lista(ref="lista", v-show="visivel")
      .empresa-item.d-flex.align-center.px-4.py-0(
        v-for="empresa in empresas",
        :key="empresa.temporaria ? 'temporaria' : empresa.codigoempresa",
        :ref="empresa.temporaria ? 'itemTemporario' : null",
        :class="{ 'empresa-item--selecionada': selecionado(empresa), 'empresa-item--temporaria': empresa.temporaria, 'empresa-item--inativa': empresa.empresaativa === false }",
        :style="clickable ? 'cursor: pointer' : ''",
        @click="$emit('select', empresa)"
      )
        .empresa-numero(
          :class="{ 'empresa-numero--inativa': empresa.empresaativa === false }",
          :style="estiloNumero(empresa)"
        ) {{ empresa.codigoempresa }}
        .empresa-info
          .empresa-nome
            | {{ empresa.fantasia || empresa.razaosocial }}
            v-chip.empresa-inativa-badge.ml-2(
              v-if="empresa.empresaativa === false",
              x-small,
              color="grey lighten-2",
              text-color="grey darken-2"
            ) INATIVA
          .empresa-detalhes
            v-chip.persona-badge.mr-2(x-small, outlined, :title="empresa.dstipoempresa")
              span.persona-texto {{ empresa.dstipoempresa }}
            .empresa-cnpj {{ empresa.cnpjcpf }}
        v-spacer
        template(v-if="badges")
          v-chip.empresa-count-badge(
            v-for="(badge, idx) in badges(empresa)",
            :key="idx",
            x-small,
            :color="badge.color",
            :text-color="badge.textColor",
            v-bind:title="badge.titulo"
          )
            | {{ badge.texto }}
            v-icon.ml-1(v-if="badge.icone", x-small, left) {{ badge.icone }}
        v-chip.empresa-count-badge(
          v-else,
          x-small,
          :color="chipVariant(empresa).color",
          :text-color="chipVariant(empresa).textColor",
          v-bind:title="`${contagem(empresa)} ${sufixoContagem}`"
        )
          | {{ contagem(empresa) }}
          v-icon.ml-1(x-small, left) {{ iconeContagem }}
        v-tooltip(bottom)
          template(v-slot:activator="{ on, attrs }")
            v-btn.preview-empresa-btn.ml-2(
              v-if="podeVisualizar",
              v-bind="attrs",
              v-on="on",
              icon,
              x-small,
              @click.stop="$emit('preview', empresa)"
            )
              v-icon(small) mdi-eye
          span Visualizar
        v-tooltip(bottom)
          template(v-slot:activator="{ on, attrs }")
            v-btn.inativar-empresa-btn.ml-2(
              v-if="podeInativarEmpresa",
              v-bind="attrs",
              v-on="on",
              icon,
              x-small,
              :disabled="inativarDesabilitado(empresa)",
              @click.stop="$emit('inativar-empresa', empresa)"
            )
              v-icon(small) mdi-domain-off
          span {{ textoInativarEmpresa }}
        v-tooltip(bottom)
          template(v-slot:activator="{ on, attrs }")
            v-btn.clonar-empresa-btn.ml-1(
              v-if="podeClonarEmpresa",
              v-bind="attrs",
              v-on="on",
              icon,
              x-small,
              :disabled="clonarDesabilitado(empresa)",
              @click.stop="$emit('clonar-empresa', empresa)"
            )
              v-icon(small) mdi-content-copy
          span {{ textoClonarEmpresa }}
</template>

<script>
export default {
  name: "EmpresasCard",

  computed: {
    // Empresa marcada como selecionada na lista (a mesma que recebe o destaque).
    empresaSelecionada() {
      return this.empresas.find(e => this.selecionado(e)) || null;
    },

    // Registro provisório (só em memória), marcado com "temporaria".
    temItemTemporario() {
      return this.empresas.some(e => e.temporaria);
    }
  },

  methods: {
    estiloNumero(empresa) {
      const cor = this.numeroCor(empresa);
      return cor ? { backgroundColor: cor } : null;
    },

    // Uso sob demanda (o pai chama via ref): rola só o contêiner da lista, sem mexer nos
    // ancestrais, até deixar a linha selecionada no centro da área visível.
    rolarParaSelecionada() {
      const lista = this.$refs.lista;
      if (!lista || lista.offsetParent === null) return;
      const item = lista.querySelector(".empresa-item--selecionada");
      if (!item) return;

      const topo = item.getBoundingClientRect().top - lista.getBoundingClientRect().top;
      const alvo = lista.scrollTop + topo - (lista.clientHeight - item.offsetHeight) / 2;
      lista.scrollTop = Math.max(0, alvo);
    }
  },

  watch: {
    // A lista tem altura limitada; leva o registro novo para a área visível.
    temItemTemporario(presente) {
      if (!presente) return;
      this.$nextTick(() => {
        const [item] = this.$refs.itemTemporario || [];
        if (item && item.scrollIntoView) item.scrollIntoView({ block: "nearest" });
      });
    }
  },

  props: {
    titulo: {
      type: String,
      default: "Empresas"
    },
    subtitulo: {
      type: String,
      default: ""
    },
    empresas: {
      type: Array,
      default: () => []
    },
    carregando: {
      type: Boolean,
      default: false
    },
    visivel: {
      type: Boolean,
      default: true
    },
    temCliente: {
      type: Boolean,
      default: false
    },
    podeClonar: {
      type: Boolean,
      default: false
    },
    podeVisualizar: {
      type: Boolean,
      default: false
    },
    // Botão de clonar em cada linha, ao lado do olho. Emite "clonar-empresa".
    podeClonarEmpresa: {
      type: Boolean,
      default: false
    },
    textoClonarEmpresa: {
      type: String,
      default: "Clonar a partir desta empresa"
    },
    clonarDesabilitado: {
      type: Function,
      default: empresa => !!empresa.temporaria || empresa.empresaativa === false
    },
    // Botão de inativar em cada linha de empresa ativa. Emite "inativar-empresa".
    podeInativarEmpresa: {
      type: Boolean,
      default: false
    },
    textoInativarEmpresa: {
      type: String,
      default: "Inativar empresa"
    },
    // Recebe a empresa e devolve true quando o botão de inativar deve ficar desabilitado.
    inativarDesabilitado: {
      type: Function,
      default: empresa => !!empresa.temporaria || empresa.empresaativa === false
    },
    // Recebe a empresa e devolve a cor da bolinha do número (ou null para manter o azul padrão).
    numeroCor: {
      type: Function,
      default: () => null
    },
    selecionado: {
      type: Function,
      default: () => false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    contagem: {
      type: Function,
      default: () => 0
    },
    chipVariant: {
      type: Function,
      default: () => ({ color: "blue lighten-4", textColor: "blue darken-3" })
    },
    // Recebe a empresa e devolve a lista de badges a exibir, no formato
    // { texto, icone, titulo, color, textColor }. Quando ausente, o card
    // mantém o chip único alimentado por "contagem" e "chipVariant".
    badges: {
      type: Function,
      default: null
    },
    iconeContagem: {
      type: String,
      default: "mdi-format-list-bulleted"
    },
    sufixoContagem: {
      type: String,
      default: "item(ns)"
    },
    textoClonar: {
      type: String,
      default: "Clonar"
    },
    textoVazioTitulo: {
      type: String,
      default: "Selecione um cliente"
    },
    textoVazioSubtitulo: {
      type: String,
      default: "Selecione um cliente ao lado para visualizar as empresas"
    }
  }
};
</script>

<style scoped>
.custom-card {
  width: 100%;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #d9e2ec !important;
  border-radius: 18px 6px 18px 6px !important;
  box-shadow: 0 2px 8px rgba(15, 42, 77, 0.08) !important;
}

.gap-1 {
  gap: 4px;
}

.btn-action {
  height: 30px !important;
  min-width: 0 !important;
  padding: 0 12px !important;
  font-size: 12px !important;
  letter-spacing: 0.01em !important;
  border: 1px solid #d0d7de !important;
  color: #57606a !important;
  background-color: #ffffff !important;
  font-weight: 500 !important;
  border-radius: 6px !important;
}

.btn-action--icone {
  width: 30px !important;
  padding: 0 !important;
}

.text-none {
  text-transform: none !important;
}

.empresas-header {
  flex: 0 0 auto;
  min-height: 56px;
  padding: 0 18px;
  display: flex;
  align-items: center;
}

.gap-2 {
  gap: 8px;
}

.empresas-titulo {
  color: #0f2b57;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.empresas-lista {
  background-color: #f8fbff;
  max-height: 210px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
}

.empresa-item {
  border-bottom: 1px solid #e8eef4;
  transition: background-color 0.15s;
}

.empresa-item:hover {
  background-color: #edf4fc;
}

.empresa-item:last-child {
  border-bottom: none;
}

.empresa-item--selecionada {
  background-color: #e3f2fd !important;
  box-shadow: inset 0 0 0 1px #1976d2;
  border-bottom-color: transparent;
}

.empresa-item--temporaria,
.empresa-item--temporaria:hover {
  background-color: #fff3e0 !important;
  box-shadow: inset 0 0 0 1px #fb8c00;
}

.empresa-numero {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #1976d2;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.empresa-numero--resumo {
  margin-right: 8px;
}

.empresa-selecionada-resumo {
  min-width: 0;
  max-width: 280px;
}

.empresa-selecionada-nome {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  min-width: 0;
}

.empresa-numero--inativa {
  background-color: #b0b9c4 !important;
}

.empresa-info {
  flex: 1;
  min-width: 0;
}

.empresa-nome {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
}

.empresa-detalhes {
  display: flex;
  align-items: center;
  min-width: 0;
}

.empresa-cnpj {
  font-size: 11px;
  color: #777;
  white-space: nowrap;
  flex-shrink: 0;
}

.persona-texto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empresa-item--inativa,
.empresa-item--inativa:hover {
  background-color: #f3f5f8 !important;
}

.empresa-item--inativa .empresa-nome {
  color: #5f6b7a !important;
}

.empresa-item--inativa .empresa-cnpj,
.empresa-item--inativa .persona-badge {
  color: #6b7787 !important;
}

.empresa-item--inativa .persona-badge {
  border-color: #aab4c0 !important;
}

.empresa-inativa-badge {
  font-weight: 700;
  font-size: 10px !important;
  height: 18px !important;
  padding: 0 8px !important;
}

.persona-badge {
  min-width: 0;
  flex-shrink: 1;
  font-weight: 600;
  font-size: 10px !important;
  height: 18px !important;
  border-color: #9e9e9e !important;
  color: #616161 !important;
}

.empresa-count-badge {
  font-weight: 700;
  font-size: 10px !important;
  height: 18px !important;
  padding: 0 8px !important;
  margin-left: 4px;
}

.empresa-count-badge .v-icon--left {
  margin-right: 4px;
}

.empty-state-icon {
  opacity: 0.5;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 600;
  color: #616161;
}

.empty-state-subtitle {
  font-size: 12px;
  color: #9e9e9e;
  max-width: 260px;
  margin: 0 auto;
  line-height: 1.4;
}

.empresas-loading-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}
</style>
